// pages/sites/SiteList.js
import React, { useEffect, useState, useCallback } from 'react'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { useGlobal } from '../../context/GlobalContext'
import api from '../../services/api'
import '../../assets/css/SiteList.css'

function SiteList() {
  const [sites, setSites] = useState([])
  const [editingSiteId, setEditingSiteId] = useState(null)
  const [editedName, setEditedName] = useState('')
  const [editedAddress, setEditedAddress] = useState('')
  const [mapRef, setMapRef] = useState(null)
  const { showLoading, hideLoading, showMessage } = useGlobal()
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places']
  })

  const fetchSites = async () => {
    showLoading()
    try {
      const res = await api.get('/site/all')
      setSites(res.data.sites || [])
    } catch {
      showMessage('Error fetching sites', 'error')
    }
    hideLoading()
  }

  useEffect(() => {
    fetchSites()
  }, [])

  const onLoadMap = useCallback(map => {
    setMapRef(map)
  }, [])

  const onUnmountMap = useCallback(() => {
    setMapRef(null)
  }, [])

  useEffect(() => {
    if (isLoaded && mapRef && sites.length) {
      const bounds = new window.google.maps.LatLngBounds()
      sites.forEach(site => {
        const lat = parseFloat(site.lat) || 0
        const lng = parseFloat(site.lng) || 0
        bounds.extend({ lat, lng })
      })
      mapRef.fitBounds(bounds)
    }
  }, [isLoaded, mapRef, sites])

  const handleEditClick = site => {
    setEditingSiteId(site._id)
    setEditedName(site.plainName)
    setEditedAddress(site.address)
  }

  const handleCancelEdit = () => {
    setEditingSiteId(null)
    setEditedName('')
    setEditedAddress('')
  }

  const handleSaveEdit = async siteId => {
    showLoading()
    try {
      await api.put(`/site/update/${siteId}`, {
        name: editedName,
        address: editedAddress
      })
      showMessage('Site updated successfully', 'success')
      await fetchSites()
      handleCancelEdit()
    } catch {
      showMessage('Error updating site', 'error')
    }
    hideLoading()
  }

  if (loadError) {
    return (
      <div className="sites-container">
        <h2>Sites</h2>
        <p>Map failed to load.</p>
      </div>
    )
  }

  return (
    <div className="sites-container">
      <h2>All Sites</h2>
      <div className="sites-list">
        {sites.map(site => (
          <div key={site._id} className="site-item">
            {editingSiteId === site._id ? (
              <div className="site-edit-form">
                <input
                  value={editedName}
                  onChange={e => setEditedName(e.target.value)}
                />
                <input
                  value={editedAddress}
                  onChange={e => setEditedAddress(e.target.value)}
                />
                <button onClick={() => handleSaveEdit(site._id)}>Save</button>
                <button onClick={handleCancelEdit}>Cancel</button>
              </div>
            ) : (
              <div className="site-details">
                <span>{site.plainName}</span>
                <span>{site.address}</span>
                <button onClick={() => handleEditClick(site)}>Edit</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {isLoaded && (
        <div className="sites-map-container">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={{ lat: 0, lng: 0 }}
            zoom={2}
            onLoad={onLoadMap}
            onUnmount={onUnmountMap}
            options={{ disableDefaultUI: false, zoomControl: true }}
          >
            {sites.map(site => (
              <Marker
                key={site._id}
                position={{ lat: parseFloat(site.lat) || 0, lng: parseFloat(site.lng) || 0 }}
              />
            ))}
          </GoogleMap>
        </div>
      )}
    </div>
  )
}

export default SiteList